import React, { lazy } from 'react';
import { Route, Switch, useLocation, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
// project imports
import MainLayout from './../layout/MainLayout';
import Loadable from '../ui-component/Loadable';

import { getAccessToken } from '../services/authService';
import { consumeDeliberateLogoutMarker, hasEverLoggedIn } from '../utils/userPrefs';

import toast from 'react-hot-toast';

// dashboard routing
const DashboardDefault = Loadable(lazy(() => import('../views/dashboard')));

// utilities routing
const UtilsTypography = Loadable(lazy(() => import('../views/utilities/Typography')));
const UtilsColor = Loadable(lazy(() => import('../views/utilities/Color')));
const UtilsShadow = Loadable(lazy(() => import('../views/utilities/Shadow')));
const UtilsMaterialIcons = Loadable(lazy(() => import('../views/utilities/MaterialIcons')));
const UtilsTablerIcons = Loadable(lazy(() => import('../views/utilities/TablerIcons')));

const UsersComponent = Loadable(lazy(() => import('../views/users')));
const UserDetailsComponent = Loadable(lazy(() => import('../views/users/userDetails')));
const SuppliersComponent = Loadable(lazy(() => import('../views/suppliers')));
// const SupplierDetailsComponent = Loadable(lazy(() => import('../views/suppliers/supplierDetails')));
const SurveyTemplatesComponent = Loadable(lazy(() => import('../views/survey-templates')));
// const SurveyTemplateDetailsComponent = Loadable(lazy(() => import('../views/survey-templates/details')));
const VideosComponent = Loadable(lazy(() => import('../views/videos')));
const WatchLogsComponent = Loadable(lazy(() => import('../views/watch-logs')));
const ErrorLogsComponent = Loadable(lazy(() => import('../views/error-logs')));
const DemoShell = Loadable(lazy(() => import('../views/demo')));
const ActivityMonitorComponent = Loadable(lazy(() => import('../views/activity')));

//-----------------------|| MAIN ROUTING ||-----------------------//

const routePaths = {
    dashboard: '/dashboard',
    users: '/users',
    userById: '/users/:id',
    projectSuppliers: '/project/:projectId/suppliers',
    projectSurveyTemplates: '/project/:projectId/survey-templates',
    projectFiles: '/project/:projectId/files',
    projectRoot: '/project/:projectId',
    watchLogs: '/logs/watch-logs',
    errorLogs: '/logs/error-logs',
    demo: '/demo',
    // Admin only — the page redirects other accounts and the API refuses them.
    activityMonitor: '/admin/activity',
};

// Hoisted out of MainRoutes — defining PrivateRoute inside the parent's render created a
// fresh component identity on every parent re-render, which forced React to unmount and
// remount the entire route subtree (and reset every page's local state) on any state
// change. Also, the previous `toast.error(...)` lived in the render body and fired once
// per render whenever the token was missing, piling up toasts. We pass `{ id: ... }` to
// react-hot-toast so repeated calls collapse onto the same toast instead.
const PrivateRoute = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            const isAccessTokenValid = getAccessToken();
            if (!isAccessTokenValid) {
                // No token doesn't always mean "expired" — it's also true right after a
                // deliberate Logout (e.g. pressing Back afterward) and for a visitor who was
                // simply never logged in on this browser. Only show the "expired" toast when
                // neither of those explains it.
                const wasDeliberateLogout = consumeDeliberateLogoutMarker();
                if (!wasDeliberateLogout && hasEverLoggedIn()) {
                    toast.error('Your session has expired, please sign in again!', { id: 'session-expired' });
                }
                return <Redirect to={{ pathname: '/session/login' }} />;
            }
            return <Component {...props} />;
        }}
    />
);

const MainRoutes = () => {
    const location = useLocation();

    return (
        <Route
            path={[
                routePaths.dashboard,
                routePaths.userById,
                routePaths.users,
                routePaths.projectSuppliers,
                routePaths.projectSurveyTemplates,
                routePaths.projectFiles,
                routePaths.projectRoot,
                routePaths.watchLogs,
                routePaths.errorLogs,
                routePaths.demo,
                routePaths.activityMonitor,
                '/suppliers',
                '/survey-templates',
                '/files',

                '/utils/util-typography',
                '/utils/util-color',
                '/utils/util-shadow',
                '/icons/tabler-icons',
                '/icons/material-icons',

            ]}>
            <MainLayout>
                <Switch location={location} key={location.pathname}>
                    <PrivateRoute path={routePaths.dashboard} component={DashboardDefault} />
                    <PrivateRoute path={routePaths.userById} component={UserDetailsComponent} />
                    <PrivateRoute path={routePaths.users} component={UsersComponent} />

                    <PrivateRoute path={routePaths.projectSuppliers} component={SuppliersComponent} />
                    <PrivateRoute path={routePaths.projectSurveyTemplates} component={SurveyTemplatesComponent} />
                    <PrivateRoute path={routePaths.projectFiles} component={VideosComponent} />
                    <Route exact path={routePaths.projectRoot} render={({ match }) => <Redirect to={`/project/${match.params.projectId}/suppliers`} />} />

                    {/* Backward-compat: old top-level routes redirect to dashboard so user can pick a project */}
                    <Route path="/suppliers"><Redirect to="/dashboard" /></Route>
                    <Route path="/survey-templates"><Redirect to="/dashboard" /></Route>
                    <Route path="/files"><Redirect to="/dashboard" /></Route>

                    <PrivateRoute path={routePaths.watchLogs} component={WatchLogsComponent} />
                    <PrivateRoute path={routePaths.errorLogs} component={ErrorLogsComponent} />
                    <PrivateRoute path={routePaths.demo} component={DemoShell} />
                    <PrivateRoute path={routePaths.activityMonitor} component={ActivityMonitorComponent} />

                    <PrivateRoute path='/utils/util-typography' component={UtilsTypography} />
                    <PrivateRoute path='/utils/util-color' component={UtilsColor} />
                    <PrivateRoute path='/utils/util-shadow' component={UtilsShadow} />
                    <PrivateRoute path='/icons/tabler-icons' component={UtilsTablerIcons} />
                    <PrivateRoute path='/icons/material-icons' component={UtilsMaterialIcons} />
                </Switch>
            </MainLayout>
        </Route>
    );
};

const mapStateToProps = (state) => ({
    authReducer: state.authReducer
});


export default connect(mapStateToProps, null)(MainRoutes);

